import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-deprecated-imports
import { Create } from '@atlaskit/atlassian-navigation/create';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { TeamsAppAnalyticsContext } from '@atlaskit/teams-app-internal-analytics/analytics-context';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics/use-analytics-events';
import { AnalyticsEventActions, AnalyticsEventSource } from '@atlassian/ptc-common/constants';
import { messages as addPeopleMessages } from '@atlassian/ptc-common/messages';
import useAnalytics from '@atlassian/ptc-common/use-analytics';

import LazyAddPeopleDialog from '../../../../../../common/ui/InvitePeopleDialog';
import { useStandaloneFlagActions } from '../../../../../../controllers/flags';
import { useOrgIdState } from '../../../../../../controllers/org-id';
import { useSiteStandaloneApp } from '../../../../../../controllers/site-standalone-app';

const AddPeopleButtonInner = () => {
	const [, { addFlag }] = useStandaloneFlagActions();
	const intl = useIntl();
	const [{ cloudId }] = useSiteStandaloneApp();
	const [{ orgId }] = useOrgIdState();
	const [isAddPeopleDialogOpen, setIsAddPeopleDialogOpen] = useState(false);

	const analytics = useAnalytics();
	const { fireEvent } = useAnalyticsEvents();

	const onClick = useCallback(() => {
		if (fg('ptc-enable-ptc-common-analytics-refactor')) {
			fireEvent('ui.button.clicked.addPeopleButton', {
				isPrimary: true,
			});
		} else {
			analytics.pushUIEvent({
				source: AnalyticsEventSource.PEOPLE_HOME,
				actionSubject: 'button',
				actionSubjectId: 'addPeopleButton',
				action: AnalyticsEventActions.CLICKED,
				attributes: { isPrimary: true },
			});
		}

		setIsAddPeopleDialogOpen(true);
	}, [analytics, fireEvent]);

	const dialog = isAddPeopleDialogOpen && (
		<ModalTransition>
			<LazyAddPeopleDialog
				source="peopleDirectoryMenuButton"
				addFlag={addFlag}
				cloudId={cloudId}
				orgId={orgId}
				onClose={() => setIsAddPeopleDialogOpen(false)}
			/>
		</ModalTransition>
	);

	if (fg('ptc-enable-ptc-common-analytics-refactor')) {
		return (
			<TeamsAppAnalyticsContext data={{ source: AnalyticsEventSource.PEOPLE_HOME }}>
				<>
					<Create
						text={intl.formatMessage(addPeopleMessages.addPeopleButtonLabel)}
						onClick={onClick}
					/>
					{dialog}
				</>
			</TeamsAppAnalyticsContext>
		);
	}

	return (
		<>
			<Create text={intl.formatMessage(addPeopleMessages.addPeopleButtonLabel)} onClick={onClick} />
			{dialog}
		</>
	);
};

export const AddPeopleButton = (): React.JSX.Element => {
	if (fg('ptc-enable-ptc-common-analytics-refactor')) {
		return (
			<TeamsAppAnalyticsContext data={{ source: AnalyticsEventSource.PEOPLE_HOME }}>
				<AddPeopleButtonInner />
			</TeamsAppAnalyticsContext>
		);
	}
	return <AddPeopleButtonInner />;
};
